import React from "react";
import { CheckCircle2, Smartphone, X } from "lucide-react";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { ActionButton, joinClasses } from "./ResultsPrimitives.jsx";
import {
  DRIVER_PORTAL_FLUORESCENT_BADGE_CLASS_NAME,
  DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME,
} from "../utils/driverPortalPublicationStyles.js";
import {
  buildTodayStalePortalDispatchUrl,
  buildTodayStalePortalRouteKey,
  formatTodayStalePortalRouteLabel,
} from "../lib/todayStalePortalRoutesApi.js";

const BANNER_VIEWPORT_INSET_PX = 8;

function isInteractiveBannerTarget(target) {
  return target instanceof Element && Boolean(target.closest("button, a, input, textarea, select"));
}

function clampBannerOffset(nextX, nextY, element, currentOffset) {
  if (!(element instanceof HTMLElement) || typeof window === "undefined") {
    return { x: nextX, y: nextY };
  }

  const rect = element.getBoundingClientRect();
  const minX = currentOffset.x - rect.left + BANNER_VIEWPORT_INSET_PX;
  const maxX = currentOffset.x + (window.innerWidth - rect.right) - BANNER_VIEWPORT_INSET_PX;
  const minY = currentOffset.y - rect.top + BANNER_VIEWPORT_INSET_PX;
  const maxY = currentOffset.y + (window.innerHeight - rect.bottom) - BANNER_VIEWPORT_INSET_PX;

  return {
    x: Math.min(Math.max(nextX, Math.min(minX, maxX)), Math.max(minX, maxX)),
    y: Math.min(Math.max(nextY, Math.min(minY, maxY)), Math.max(minY, maxY)),
  };
}

function useTodayStalePortalBannerDrag() {
  const panelRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 0, y: 0 });
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);

  const updateOffset = React.useCallback((nextOffset) => {
    offsetRef.current = nextOffset;
    setOffset(nextOffset);
  }, []);

  const handlePointerDown = React.useCallback((event) => {
    if (event.button !== 0 || isInteractiveBannerTarget(event.target)) {
      return;
    }

    const panelElement = panelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    dragRef.current = {
      originX: offsetRef.current.x,
      originY: offsetRef.current.y,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setIsDragging(true);

    if (typeof event.currentTarget.setPointerCapture === "function") {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
  }, []);

  const handlePointerMove = React.useCallback((event) => {
    const dragState = dragRef.current;
    if (!dragState || event.pointerId !== dragState.pointerId) {
      return;
    }

    const panelElement = panelRef.current;
    updateOffset(clampBannerOffset(
      dragState.originX + (event.clientX - dragState.startX),
      dragState.originY + (event.clientY - dragState.startY),
      panelElement,
      offsetRef.current,
    ));
  }, [updateOffset]);

  const handlePointerEnd = React.useCallback((event) => {
    const dragState = dragRef.current;
    if (!dragState || event.pointerId !== dragState.pointerId) {
      return;
    }

    if (event.currentTarget instanceof HTMLElement && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore environments that already released the pointer.
      }
    }

    dragRef.current = null;
    setIsDragging(false);
  }, []);

  return {
    handlePointerDown,
    handlePointerEnd,
    handlePointerMove,
    isDragging,
    offset,
    panelRef,
  };
}

export default function TodayStalePortalRoutesBanner({
  className = "",
  dispatchDate = "",
  onDismiss,
  onOpenDispatch,
  onPublishRoute,
  publishingRouteKey = "",
  routes = [],
  successNotices = [],
}) {
  const {
    handlePointerDown,
    handlePointerEnd,
    handlePointerMove,
    isDragging,
    offset,
    panelRef,
  } = useTodayStalePortalBannerDrag();
  const [isDismissConfirmOpen, setIsDismissConfirmOpen] = React.useState(false);
  const visibleRoutes = Array.isArray(routes) ? routes.filter((route) => route?.truckId) : [];
  const visibleSuccessNotices = Array.isArray(successNotices)
    ? successNotices.filter((notice) => notice?.key)
    : [];
  const staleRouteKeys = new Set(visibleRoutes.map((route) => buildTodayStalePortalRouteKey(route)));
  const leftoverSuccessNotices = visibleSuccessNotices.filter((notice) => !staleRouteKeys.has(notice.key));
  const successNoticeByKey = new Map(visibleSuccessNotices.map((notice) => [notice.key, notice]));

  if (visibleRoutes.length === 0 && leftoverSuccessNotices.length === 0) {
    return null;
  }

  const pendingCount = visibleRoutes.filter((route) => (
    !successNoticeByKey.has(buildTodayStalePortalRouteKey(route))
  )).length;
  const hasOnlySuccess = pendingCount === 0 && leftoverSuccessNotices.length > 0;

  const requestDismiss = () => {
    if (typeof onDismiss !== "function") {
      return;
    }
    if (hasOnlySuccess) {
      onDismiss();
      return;
    }
    setIsDismissConfirmOpen(true);
  };

  return (
    <>
    <section
      ref={panelRef}
      aria-live="assertive"
      aria-label={hasOnlySuccess ? "Portail chauffeur en ligne" : "Portail chauffeur à republier"}
      className={joinClasses([
        "rounded-[26px] border-2 px-4 py-3.5 shadow-[0_20px_44px_rgba(6,78,59,0.18)]",
        "border-[#0B6B3A] bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(239,255,202,0.96))]",
        className,
      ])}
      data-today-stale-portal-banner="true"
      style={{
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        willChange: isDragging ? "transform" : undefined,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className={joinClasses([
            "mt-0.5 flex h-11 w-11 flex-none items-center justify-center rounded-full border",
            hasOnlySuccess ? "" : "animate-today-stale-portal-blink",
            DRIVER_PORTAL_FLUORESCENT_BADGE_CLASS_NAME,
          ])}
        >
          {hasOnlySuccess ? (
            <CheckCircle2 size={18} aria-hidden="true" className="text-[#064E3B]" strokeWidth={2.2} />
          ) : (
            <Smartphone
              size={18}
              aria-hidden="true"
              className={DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME}
              strokeWidth={2.2}
            />
          )}
        </span>

        <div className="grid min-w-0 flex-1 gap-2">
          <div className="flex items-start gap-2">
            <div
              className={joinClasses([
                "grid min-w-0 flex-1 touch-none select-none gap-0.5",
                isDragging ? "cursor-grabbing" : "cursor-grab",
              ])}
              data-today-stale-portal-drag-handle="true"
              onPointerCancel={handlePointerEnd}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
            >
              <p className="m-0 text-[0.72rem] font-black uppercase tracking-[0.18em] text-[#064E3B]">
                Portail chauffeur
              </p>
              <h2 className="m-0 text-[0.98rem] font-bold leading-5 text-[#064E3B]">
                {hasOnlySuccess
                  ? (leftoverSuccessNotices.length > 1 ? "Tournées en ligne" : "Tournée en ligne")
                  : (pendingCount > 1 ? "Tournées à republier" : "Tournée à republier")}
              </h2>
              <p className="m-0 text-[13px] leading-5 text-[#14532D]">
                {hasOnlySuccess
                  ? "C'est à nouveau en ligne. Le camionneur voit sa tournée."
                  : pendingCount > 1
                    ? "Des itinéraires du jour ne sont plus visibles pour les camionneurs. Republiez avant qu'ils vous contactent."
                    : "L'itinéraire n'est plus visible pour le camionneur. Republiez avant qu'il vous contacte."}
              </p>
            </div>
            <button
              type="button"
              aria-label="Fermer le rappel portail"
              className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full text-[#064E3B] transition-colors hover:bg-[#064E3B]/10"
              data-today-stale-portal-dismiss="true"
              onClick={requestDismiss}
            >
              <X size={16} aria-hidden="true" strokeWidth={2.2} />
            </button>
          </div>

          <div className="grid gap-2">
            {visibleRoutes.map((route) => {
              const routeKey = buildTodayStalePortalRouteKey(route);
              const successNotice = successNoticeByKey.get(routeKey);
              const isPublishing = publishingRouteKey === routeKey;
              const dispatchUrl = buildTodayStalePortalDispatchUrl(
                route.dispatchDate || dispatchDate,
                route.dispatchPeriod,
              );

              return (
                <div
                  key={routeKey}
                  className="grid gap-2 rounded-[16px] border border-[#0B6B3A]/20 bg-white/80 px-3 py-2.5"
                  data-today-stale-portal-route={route.truckId}
                >
                  <p className="m-0 text-[13px] font-semibold leading-5 text-slate-800">
                    {formatTodayStalePortalRouteLabel(route)}
                  </p>
                  {successNotice ? (
                    <p className="m-0 text-[12px] font-semibold leading-4.5 text-[#14532D]">
                      {successNotice.message || "C'est à nouveau en ligne."}
                    </p>
                  ) : (
                    <>
                      {route.withdrawalReasonLabel ? (
                        <p className="m-0 text-[12px] leading-4.5 text-slate-600">
                          Cause : {route.withdrawalReasonLabel}
                        </p>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-2">
                        <ActionButton
                          size="pill"
                          tone="optimizer"
                          disabled={Boolean(publishingRouteKey)}
                          isLoading={isPublishing}
                          loadingLabel="Publication..."
                          onClick={() => {
                            void onPublishRoute?.(route);
                          }}
                        >
                          Publier
                        </ActionButton>
                        <ActionButton
                          size="pill"
                          tone="ghost"
                          onClick={() => {
                            if (typeof onOpenDispatch === "function") {
                              onOpenDispatch(dispatchUrl, route);
                              return;
                            }
                            window.location.assign(dispatchUrl);
                          }}
                        >
                          Ouvrir le dispatch
                        </ActionButton>
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {leftoverSuccessNotices.map((notice) => (
              <div
                key={notice.key}
                className="grid gap-1 rounded-[16px] border border-[#0B6B3A]/20 bg-white/80 px-3 py-2.5"
                data-today-stale-portal-success={notice.key}
              >
                <p className="m-0 text-[13px] font-semibold leading-5 text-slate-800">
                  {notice.label}
                </p>
                <p className="m-0 text-[12px] font-semibold leading-4.5 text-[#14532D]">
                  {notice.message || "C'est à nouveau en ligne."}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    <ConfirmationModal
      cancelLabel="Annuler"
      confirmLabel="Fermer quand même"
      contentDensity="compact"
      description={pendingCount > 1
        ? "Les camionneurs ne voient plus ces tournées. Fermez seulement si vous ne voulez plus les republier maintenant."
        : "Le camionneur ne voit plus sa tournée. Fermez seulement si vous ne voulez plus la republier maintenant."}
      eyebrow="Portail chauffeur"
      isOpen={isDismissConfirmOpen}
      onCancel={() => setIsDismissConfirmOpen(false)}
      onClose={() => setIsDismissConfirmOpen(false)}
      onConfirm={() => {
        setIsDismissConfirmOpen(false);
        if (typeof onDismiss === "function") {
          onDismiss();
        }
      }}
      panelSize="compact"
      title="Fermer sans republier ?"
      tone="danger"
      warningMessage={pendingCount > 1
        ? "Les itinéraires ne sont plus visibles pour les camionneurs tant qu'ils ne sont pas republiés."
        : "L'itinéraire n'est plus visible pour le camionneur tant qu'il n'est pas republié."}
    />
    </>
  );
}
